import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api.ts';
import { messageErreur, useSession } from './session.tsx';

/**
 * Chargement de données.
 *
 * ── Pourquoi un crochet maison plutôt que TanStack Query ────────────────────
 * Les écrans de cette application chargent des listes courtes, propres à un
 * enseignant, et les rechargent après chaque écriture. Il n'y a ni cache
 * partagé entre écrans, ni pagination infinie, ni invalidation croisée : la
 * surface qui justifierait une bibliothèque de cache n'existe pas.
 *
 * ── Ce qui, en revanche, ne se néglige pas ──────────────────────────────────
 * 1. **L'annulation.** Un enseignant qui change d'onglet pendant un chargement
 *    ne doit pas voir la réponse arriver dans l'écran suivant.
 * 2. **L'ordre des réponses.** Deux chargements lancés coup sur coup peuvent
 *    revenir dans le désordre ; sans garde, l'ancien écrase le récent.
 * 3. **L'état d'erreur distinct de l'état vide.** « Aucun élève » et « le
 *    chargement a échoué » ne se disent pas de la même façon.
 */

export interface EtatDonnees<T> {
  donnees: T | null;
  chargement: boolean;
  erreur: string | null;
  /**
   * Code HTTP de l'échec, quand il y en a un.
   *
   * Le message seul ne permet pas de distinguer « cet espace n'a pas encore
   * d'année scolaire » (404, normal) de « ce compte appartient à un autre
   * espace » (403, une impasse) — deux situations qui appellent des écrans
   * opposés.
   */
  statutErreur: number | null;
  /** Relance la requête, par exemple après une écriture. */
  recharger: () => Promise<void>;
  /** Remplace les données localement, sans aller-retour réseau. */
  definir: (valeur: T) => void;
}

export function useDonnees<T>(
  chemin: string | null,
  dependances: readonly unknown[] = [],
): EtatDonnees<T> {
  const { api } = useSession();
  const [donnees, setDonnees] = useState<T | null>(null);
  const [chargement, setChargement] = useState(chemin !== null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [statutErreur, setStatutErreur] = useState<number | null>(null);

  // Numéro de la requête en cours : seule la plus récente a le droit d'écrire
  // dans l'état. Sans ce compteur, deux chargements rapprochés peuvent revenir
  // dans le désordre et afficher les données périmées.
  const generation = useRef(0);
  const enCours = useRef<AbortController | null>(null);

  const charger = useCallback(async () => {
    if (chemin === null) {
      setChargement(false);
      return;
    }
    const mienne = ++generation.current;
    enCours.current?.abort();
    const controleur = new AbortController();
    enCours.current = controleur;

    setChargement(true);
    setErreur(null);
    setStatutErreur(null);
    try {
      const recu = await api.requete<T>(chemin, { signal: controleur.signal });
      if (mienne !== generation.current) return;
      setDonnees(recu);
    } catch (e) {
      // Une annulation n'est pas une erreur : l'utilisateur est simplement
      // passé à autre chose. L'afficher serait un faux positif inquiétant.
      if (controleur.signal.aborted || mienne !== generation.current) return;
      setErreur(messageErreur(e));
      setStatutErreur(e instanceof ApiError ? e.status : null);
    } finally {
      if (mienne === generation.current) setChargement(false);
    }
    // Les dépendances sont fournies par l'appelant : le chemin seul ne suffit
    // pas quand la requête doit être rejouée après un changement d'année.
  }, [api, chemin, ...dependances]);

  useEffect(() => {
    void charger();
    return () => enCours.current?.abort();
  }, [charger]);

  return { donnees, chargement, erreur, statutErreur, recharger: charger, definir: setDonnees };
}

/**
 * Exécute une écriture en gérant l'état « en cours » et le message d'erreur.
 *
 * Le retour indique la RÉUSSITE, il n'est pas le résultat : c'est ce qui permet
 * d'écrire `if (await executer(...)) fermerLeFormulaire();` sans avaler
 * silencieusement un échec.
 */
export function useEcriture() {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const executer = useCallback(async (action: () => Promise<unknown>): Promise<boolean> => {
    setErreur(null);
    setEnCours(true);
    try {
      await action();
      return true;
    } catch (e) {
      setErreur(messageErreur(e));
      return false;
    } finally {
      setEnCours(false);
    }
  }, []);

  return { executer, enCours, erreur, effacerErreur: () => setErreur(null) };
}
